import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Settings, X } from "lucide-react";
import { loadGoogleAds, updateConsent } from "@/lib/googleAds";

type CookieConsent = "all" | "essential" | null;

function getConsent(): CookieConsent {
  const v = localStorage.getItem("gn_cookie_consent");
  if (v === "all" || v === "essential") return v;
  return null;
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [functional, setFunctional] = useState(false);

  useEffect(() => {
    if (!getConsent()) setVisible(true);
    const onReset = () => setVisible(true);
    window.addEventListener("gn-cookie-reset", onReset);
    return () => window.removeEventListener("gn-cookie-reset", onReset);
  }, []);

  const accept = (type: CookieConsent) => {
    if (type) localStorage.setItem("gn_cookie_consent", type);
    if (type === "all") {
      loadGoogleAds();
    } else {
      updateConsent("denied");
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4"
      >
        <div className="mx-auto max-w-2xl rounded-xl border border-border bg-card shadow-lg p-5">
          {!showManage ? (
            <>
              <div className="flex items-start gap-3 mb-4">
                <Cookie className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  We use cookies to improve your experience, analyse traffic, and enhance functionality. You can accept or manage your preferences.{" "}
                  <Link to="/cookies" className="text-primary underline underline-offset-2">Read our Cookie Policy</Link>
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="hero" onClick={() => accept("all")}>
                  Accept All
                </Button>
                <Button size="sm" variant="outline" onClick={() => accept("essential")}>
                  Reject Non-Essential
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowManage(true)}>
                  <Settings className="h-3.5 w-3.5 mr-1" /> Manage Preferences
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-foreground text-sm">Cookie Preferences</h3>
                <button onClick={() => setShowManage(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-3 mb-4">
                <label className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Essential</p>
                    <p className="text-xs text-muted-foreground">Required for the site to function</p>
                  </div>
                  <span className="text-xs text-muted-foreground">Always on</span>
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-sm font-medium text-foreground">Analytics</p>
                    <p className="text-xs text-muted-foreground">Help us understand usage patterns</p>
                  </div>
                  <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="h-4 w-4 rounded border-border accent-primary" />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-sm font-medium text-foreground">Functional</p>
                    <p className="text-xs text-muted-foreground">Remember your preferences</p>
                  </div>
                  <input type="checkbox" checked={functional} onChange={(e) => setFunctional(e.target.checked)} className="h-4 w-4 rounded border-border accent-primary" />
                </label>
              </div>
              <Button size="sm" variant="hero" onClick={() => accept(analytics || functional ? "all" : "essential")}>
                Save Preferences
              </Button>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/** Open cookie settings (for footer link) */
export function openCookieSettings() {
  localStorage.removeItem("gn_cookie_consent");
  window.dispatchEvent(new Event("gn-cookie-reset"));
}
